import React from 'react';
import { Home, BarChart2, FileText, Settings, PlusCircle } from 'lucide-react';
import { useSidebar } from '../../Context/SidebarContext';
import HistoryEachDates from '../HistoryEachDates';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './index.css';
import { ArrowRightToLine, ArrowLeftToLine } from "lucide-react";
import { useTheme } from '../../Context/ThemeContext';
import { useChatContext } from '../../Context/ChatContext';


// it is used to create a sidebar item
// it is used to create a link to the path
// it is used to create a sidebar icon
// it is used to create a sidebar text
// it is used to create a sidebar link
const SidebarItem = ({ icon, text, path, active }) => {
  return (
    <li className={`sidebar-item ${active ? 'active' : ''}`}>
      <Link to={path} className="sidebar-link">
        <div className="sidebar-icon">{icon}</div>
        <span className="sidebar-text">{text}</span>
      </Link>
    </li>
  );
};

const Sidebar = () => {
  const { isDarkMode } = useTheme();
  const { isOpen, toggleSidebar } = useSidebar();
  const { startNewChat } = useChatContext();
  const location = useLocation();
  const navigate = useNavigate();

  // useRef is used to create a reference to the history dates component
  // it is used to update the history dates component when the sidebar is toggled
  const historyDatesRef = React.useRef(null);
  
  // Define navigation items (active derived from current location)
  const navItems = [
    { icon: <Home size={18} />, text: 'Dashboard', path: '/' },
    { icon: <BarChart2 size={18} />, text: 'Analytics', path: '/analytics' },
    // { icon: <MessageSquare size={18} />, text: 'Chat', path: '/chat' },
    { icon: <FileText size={18} />, text: 'Documents', path: '/documents' },
    { icon: <Settings size={18} />, text: 'Settings', path: '/settings' }
  ];

  return (
    <div className={`sidebar-container ${isOpen ? 'open' : 'closed'}`}>
      <aside className="sidebar">
        <div className="sidebar-header">
          {isOpen ? (
            <>
              <div className="logo-container">
                  <img src="/assets/logo.png" alt="PinakaAI" className="logo" />
              </div>
              {/* toggle button to close sidebar */}
              <button 
                className="toggle-button" 
                onClick={toggleSidebar} 
                aria-label="Close sidebar"
              >
                <ArrowLeftToLine size={18} />
              </button>
            </>
          ) : (
            <>
            <div>
                <img src="/assets/sm.jpeg" alt="PinakaAI" className="sm-logo" />
            </div>
            {/* toggle button to open sidebar */}
            <button 
              className="toggle-button toggle-open" 
              onClick={toggleSidebar}
              aria-label="Open sidebar"
            >
              <ArrowRightToLine size={18} className="icon-arrow" />
            </button>
        </>
          )}
        </div>

        <div className="sidebar-content">
          <ul className="sidebar-menu">
            <li className="sidebar-item new-chat-item">
              <button 
                className={`sidebar-link new-chat-button ${isDarkMode ? 'dark-mode' : ''}`}
                onClick={() => {
                  // Start a fresh, blank chat from anywhere. This blanks the view
                  // and suppresses auto-reload of today's saved messages. The prior
                  // conversation is already saved in History by date and reopenable.
                  startNewChat();
                  if (location.pathname !== '/') {
                    navigate('/');
                  }
                  // Refresh the History dates list so the just-saved prior chat is
                  // listed (no-op if the panel isn't mounted yet; it loads on mount).
                  if (historyDatesRef.current?.fetchChatSessionDates) {
                    historyDatesRef.current.fetchChatSessionDates();
                  }
                }}
              >
                <div className="sidebar-icon">
                  <PlusCircle size={18} />
                </div>
                <span className="sidebar-text">New Chat</span>
              </button>
            </li>
            {navItems.map((item, index) => {
              // Active rules:
              //  - Dashboard active ONLY on '/'
              //  - Others active when pathname starts with their path
              //  - When on '/admin', none of these match, so none active
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
              return (
                <SidebarItem
                  key={index}
                  icon={item.icon}
                  text={item.text}
                  path={item.path}
                  active={isActive}
                />
              );
            })}
          </ul>
        </div>

        {isOpen && location.pathname === '/' && (
            <div className="chat-history-section">
                 <HistoryEachDates ref={historyDatesRef} />
            </div>
        )}

        <div className="sidebar-footer">
          {isOpen && <p className={`copyright ${isDarkMode ? 'dark-mode' : ''}`}>© {new Date().getFullYear()} PinakaAI</p>}
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;